/**
 * qp-pages.tsx — Queue journey PAGES: the customer-facing screens a queue
 * journey walks through (PM direction 2026-07-17). Each page is a step on the
 * journey canvas (kind "page"), designed in the Queue Page Builder
 * (QueuePageBuilder — the queuing sibling of the Landing Page Builder) and
 * rendered for real by the public join surface (QueueJoin).
 *
 * Block model (2026-07-18): a page is a STACK OF BLOCKS. Every page has one
 * non-removable functional `core` block (carrying the page's functional
 * properties — the service picker, wait estimate, contact form, etc.) plus a
 * `brand` header, and any number of content/branding blocks (heading, text,
 * image, divider, spacer, notice, button) placed around the core. Page-level
 * BRANDING (accent, background, font, logo) lives in cfg.theme. All of this is
 * additive cfg on the page step — persisted with the queue's journey, returned by
 * getPublicQueue — so no new ops and legacy pages (no cfg.blocks) render via a
 * synthesized [brand, core] default.
 *
 * QPageView is the PRESENTATIONAL renderer shared by the canvas miniature
 * (scaled down) and the builder's live phone preview. It renders at a logical
 * 375px phone width. The public surface (QueueJoin) renders the SAME blocks +
 * theme around its interactive functional core.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { alpha, Icon, T } from "@v0y4ge/jrni-ui";
import { LandingPreview, LANDING_THEMES, type LandingTheme } from "../landing/landing-builder-helpers";
import { useBranding } from "../settings/useBranding";
import { isQueueBlock, queueDesignDefault, qSampleRuntime, QueueBlockView, type QRuntime } from "./qblocks";
import { QPAGE_META, QPAGE_ORDER, qpOnAccent, type QPageId, type QPageMeta } from "./qp-meta";

import { useT } from "@v0y4ge/i18n-runtime";
// Re-exported so existing import sites (QueueJoin, QueuePageBuilder,
// journeys/flow/jf-model …) keep working — the definitions
// moved to the leaf qp-meta.ts to break the qp-pages ↔ qblocks cycle.
export { QPAGE_META, QPAGE_ORDER, qpOnAccent };
export type { QPageId, QPageMeta };

/** Contact fields — the SAME contact info as Events registration (F-71 split names). */
export type QFieldMode = "required" | "optional" | "off";
export interface QContactFields { firstName: QFieldMode; lastName: QFieldMode; mobile: QFieldMode; email: QFieldMode; }

export interface QPageCfgServices { headline: string; sub: string; layout: "list" | "grid"; showEstWait: boolean; }
export interface QPageCfgWait     { headline: string; sub: string; continueLabel: string; showBook: boolean; bookLabel: string; bookUrl: string; }
export interface QPageCfgContact  { headline: string; sub: string; fields: QContactFields; askChannel: boolean; askParty: boolean; bookingQuestions: { kind: "question" | "group"; id: string }[]; joinLabel: string; }
export interface QPageCfgWaiting  { headline: string; showPosition: boolean; showEstWait: boolean; allowMessaging: boolean; quickReplies: string[]; whileHeading: string; showBrowse: boolean; browseLabel: string; browseUrl: string; }
export interface QPageCfgCsat     { headline: string; sub: string; surveyId: string | null; buttonLabel: string; showBrowse: boolean; browseLabel: string; browseUrl: string; showReview: boolean; reviewLabel: string; reviewUrl: string; }
export type QPageCfg = QPageCfgServices | QPageCfgWait | QPageCfgContact | QPageCfgWaiting | QPageCfgCsat;

/* ── Block model ──────────────────────────────────────────────────────── */
export type QBlockType = "brand" | "core" | "heading" | "text" | "image" | "divider" | "spacer" | "notice" | "button";
export interface QBlock { uid: string; type: QBlockType; cfg: Record<string, any>; }

/** Page-level branding. Kept minimal + additive — undefined falls back to the
 * default so legacy pages look exactly as before. */
export interface QTheme { accent: string; bg: string; font: string; logoUrl: string; brandName: string; corner: number; }
export const QP_THEME_DEFAULT: QTheme = { accent: "#14131F", bg: "#F1F2F6", font: "DM Sans", logoUrl: "", brandName: "", corner: 16 };

export const QP_ACCENTS = ["#14131F", "#2D60EB", "#7C3AED", "#1F8A5B", "#C8861E", "#C0392B", "#0E8A6A", "#DB2777"];
export const QP_BGS = ["#F1F2F6", "#FFFFFF", "#FBF7F0", "#EEF2FF", "#ECFDF5", "#FDF2F8", "#0E1116"];
export const QP_FONTS = ["DM Sans", "Inter", "Source Sans Pro", "Avenir Next", "Georgia", "Helvetica Neue"];

export function qpTheme(cfg: Record<string, any> | undefined): QTheme {
  const t = (cfg?.theme ?? {}) as Partial<QTheme>;
  return {
    accent: t.accent || QP_THEME_DEFAULT.accent,
    bg: t.bg || QP_THEME_DEFAULT.bg,
    font: t.font || QP_THEME_DEFAULT.font,
    logoUrl: t.logoUrl || "",
    brandName: t.brandName || "",
    corner: typeof t.corner === "number" ? t.corner : QP_THEME_DEFAULT.corner,
  };
}
export function qpFontFamily(theme: QTheme): string {
  return theme.font ? `"${theme.font}", ${T.font}` : T.font;
}

/** The blocks of a page — the saved array, or a synthesized [brand, core]
 * default for legacy pages (no cfg.blocks). Always contains exactly one core. */
export function qpBlocks(cfg: Record<string, any> | undefined): QBlock[] {
  const b = cfg?.blocks;
  if (Array.isArray(b) && b.some((x) => x?.type === "core")) return b as QBlock[];
  return [{ uid: "brand", type: "brand", cfg: {} }, { uid: "core", type: "core", cfg: {} }];
}

let _uidN = 0;
export function qpUid() { return `qb${Date.now().toString(36)}${(_uidN++).toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`; }

/** Palette of ADDABLE content/branding blocks (core + brand are structural). */
export const QP_CONTENT_BLOCKS: { type: QBlockType; label: string; labelKey: string; hint: string; icon: ReactNode }[] = [
  { type: "heading", label: "Heading", labelKey: "queues.page.block.heading.label",  hint: "A bold line of text",       icon: <QBIcon kind="heading"/> },
  { type: "text",    label: "Text", labelKey: "queues.page.block.text.label",     hint: "A paragraph of copy",       icon: <QBIcon kind="text"/> },
  { type: "image",   label: "Image", labelKey: "queues.page.block.image.label",    hint: "A banner or logo image",    icon: <QBIcon kind="image"/> },
  { type: "notice",  label: "Notice", labelKey: "queues.page.block.notice.label",   hint: "A highlighted callout",     icon: <Icon.info size={16}/> },
  { type: "button",  label: "Button", labelKey: "queues.page.block.button.label",   hint: "A link to anywhere",        icon: <Icon.link size={16}/> },
  { type: "divider", label: "Divider", labelKey: "queues.page.block.divider.label",  hint: "A thin rule",               icon: <QBIcon kind="divider"/> },
  { type: "spacer",  label: "Spacer", labelKey: "queues.page.block.spacer.label",   hint: "Vertical breathing room",   icon: <QBIcon kind="spacer"/> },
];

function QBIcon({ kind }: { kind: string }) {
  const s = { width: 16, height: 16, display: "block" } as CSSProperties;
  if (kind === "heading") return <svg style={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 4v12M15 4v12M5 10h10"/></svg>;
  if (kind === "text") return <svg style={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 6h12M4 10h12M4 14h7"/></svg>;
  if (kind === "image") return <svg style={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="14" height="12" rx="2"/><circle cx="7.5" cy="8.5" r="1.4"/><path d="m4 15 4-4 3 3 3-3 2 2"/></svg>;
  if (kind === "divider") return <svg style={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 10h14"/></svg>;
  if (kind === "spacer") return <svg style={s} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 4h8M6 16h8M10 7v6M8 9l2-2 2 2M8 11l2 2 2-2"/></svg>;
  return null;
}

export function qpBlockDefault(type: QBlockType, t: (k: string, f?: string) => string = (_k, f) => f ?? _k): QBlock {
  const uid = qpUid();
  switch (type) {
    case "heading": return { uid, type, cfg: { text: "A warm welcome", align: "center", size: "lg" } };
    case "text":    return { uid, type, cfg: { text: "Add a short line for your customers here.", align: "center" } };
    case "image":   return { uid, type, cfg: { url: "", height: 150, radius: 14, caption: "" } };
    case "notice":  return { uid, type, cfg: { text: "Please have your reference or account number ready.", tone: "info" } };
    case "button":  return { uid, type, cfg: { label: t("queues.page.defaults.learnMore", "Learn more"), url: "", style: "outline" } };
    case "divider": return { uid, type, cfg: {} };
    case "spacer":  return { uid, type, cfg: { size: 18 } };
    default:        return { uid, type, cfg: {} };
  }
}

// ui-config — default page configs for a new journey (not tenant data)
export function qpDefaultCfg(page: QPageId): Record<string, unknown> {
  const shell = { blocks: [{ uid: "brand", type: "brand", cfg: {} }, { uid: "core", type: "core", cfg: {} }], theme: { ...QP_THEME_DEFAULT } };
  switch (page) {
    case "prebook": return { ...shell, headline: "Before you join", sub: "A few quick questions to make sure we point you to the right place.", bookingQuestions: [], continueLabel: "Continue to the queue" };
    case "services": return { ...shell, headline: "What can we help you with?", sub: "Pick a service to join the queue.", layout: "list", showEstWait: true };
    case "locations": return { ...shell, headline: "Find the shortest wait", sub: "Wait times for your service at locations near you.", maxLocations: 10, allowSortByWait: true };
    case "wait":     return { ...shell, headline: "Here's the current wait", sub: "We'll text you updates so you don't have to stand in line.", continueLabel: "Sounds good — continue", showBook: true, bookLabel: "I can't wait — book an appointment", bookUrl: "" };
    case "contact":  return { ...shell, headline: "Almost there", sub: "How should we reach you?", fields: { firstName: "required", lastName: "required", mobile: "required", email: "optional" }, askChannel: true, askParty: true, bookingQuestions: [], joinLabel: "Join the queue" };
    case "waiting":  return { ...shell, headline: "You're in the queue", showPosition: true, showEstWait: true, allowMessaging: true, quickReplies: ["I have to leave now, I'll come back later", "Running 5 minutes late", "I'm here, waiting outside"], whileHeading: "While you wait", showBrowse: true, browseLabel: "Browse our Services and Experiences", browseUrl: "" };
    case "noservice": return { ...shell, headline: "Sorry we missed you", sub: "Looks like you left before we could serve you — we'd love to make it right.", surveyId: null, buttonLabel: "Share what happened", showBook: true, bookLabel: "Book an appointment instead", bookUrl: "", showBrowse: true, browseLabel: "Browse our Services and Experiences", browseUrl: "" };
    case "csat":     return { ...shell, headline: "How did we do?", sub: "Thanks for visiting — we'd love 30 seconds of feedback.", surveyId: null, buttonLabel: "Give feedback", showBrowse: true, browseLabel: "Browse our Services and Experiences", browseUrl: "", showReview: true, reviewLabel: "Leave a Google Review", reviewUrl: "" };
  }
}

/** Context data the renderer fills the page with (live values or editor samples). */
export interface QPageCtx {
  org: string; queueName: string;
  services: string[]; estWaitMins: number; waiting: number; position: number; firstName: string;
  surveyName?: string | null; questionLabels?: string[];
}
// ui-config — sample context for the page-designer PREVIEW render only (never data)
export const QP_SAMPLE_CTX: QPageCtx = { org: "Your brand", queueName: "Walk-ins", services: ["Personal banking", "Home loans", "Wealth reviews"], estWaitMins: 15, waiting: 6, position: 4, firstName: "Alex", surveyName: null, questionLabels: [] };

/* ── Presentational building blocks (375px logical width) ─────────────── */
const P: Record<string, CSSProperties> = {
  card: { background: T.white, borderRadius: 16, boxShadow: "0 10px 30px -16px rgba(16,24,40,.25)", padding: 20, display: "flex", flexDirection: "column", gap: 14 },
  h1: { fontSize: 19, fontWeight: 800, color: T.n1, lineHeight: 1.25 },
  sub: { fontSize: 13, color: T.soft, lineHeight: 1.45 },
  input: { minHeight: 46, borderRadius: 10, border: `1px solid ${T.n4}`, background: T.white, padding: "0 12px", fontSize: 14, color: T.n5, display: "flex", alignItems: "center" },
  cta: { minHeight: 50, borderRadius: 12, background: T.n1, color: T.white, fontSize: 15, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" },
  link2: { minHeight: 48, borderRadius: 12, border: `1.5px solid ${T.border}`, background: T.white, color: T.n1, fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  label: { fontSize: 12.5, fontWeight: 600, color: T.n1 },
};

function Brand({ ctx, theme }: { ctx: QPageCtx; theme: QTheme }) {
  const name = theme.brandName || ctx.org;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 4px" }}>
      {theme.logoUrl
        ? <img src={theme.logoUrl} alt="" style={{ width: 34, height: 34, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
        : <span style={{ width: 34, height: 34, borderRadius: 10, background: theme.accent, color: qpOnAccent(theme.accent), display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>{name.slice(0, 1)}</span>}
      <div><div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{name}</div><div style={{ fontSize: 11.5, color: T.soft }}>{ctx.queueName}</div></div>
    </div>
  );
}
function FieldGhost({ label, req }: { label: string; req?: boolean }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><span style={P.label}>{label}{req ? <span style={{ color: T.red1 }}> *</span> : null}</span><div style={P.input} /></div>;
}

/** Content/branding block renderer — shared by the preview and (via QueueJoin)
 * the public surface. */
export function QContentBlock({ block, theme }: { block: QBlock; theme: QTheme }) {
  const t = useT();
  const c = block.cfg || {};
  if (block.type === "heading") {
    const size = c.size === "sm" ? 15 : c.size === "md" ? 18 : 22;
    return <div style={{ fontSize: size, fontWeight: 800, color: T.n1, lineHeight: 1.2, textAlign: c.align || "center" }}>{c.text || "Heading"}</div>;
  }
  if (block.type === "text") {
    return <div style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.5, textAlign: c.align || "center" }}>{c.text || "Text"}</div>;
  }
  if (block.type === "image") {
    if (c.url) return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}><img src={c.url} alt={c.caption || ""} style={{ width: "100%", height: c.height || 150, objectFit: "cover", borderRadius: c.radius ?? 14, display: "block" }} />{c.caption ? <div style={{ fontSize: 11.5, color: T.soft, textAlign: "center" }}>{c.caption}</div> : null}</div>;
    return <div style={{ width: "100%", height: c.height || 150, borderRadius: c.radius ?? 14, border: `1.5px dashed ${T.n4}`, background: T.n6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: T.soft }}><QBIcon kind="image"/><span style={{ fontSize: 12 }}>{t('queues.qpPages.text22')}</span></div>;
  }
  if (block.type === "notice") {
    const tone = c.tone === "success" ? { bg: "#E4F5EF", bd: "#1F8A5B", fg: "#186B47", ic: <Icon.check size={15}/> }
      : c.tone === "warn" ? { bg: "#FAF1DE", bd: "#C8861E", fg: "#8A5B12", ic: <Icon.warn size={15}/> }
      : { bg: alpha(theme.accent, 8), bd: theme.accent, fg: T.n1, ic: <Icon.info size={15}/> };
    return <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 13px", borderRadius: 11, background: tone.bg, border: `1px solid ${alpha(tone.bd, 45)}` }}><span style={{ color: tone.bd, flexShrink: 0, marginTop: 1 }}>{tone.ic}</span><span style={{ fontSize: 13, color: tone.fg, lineHeight: 1.45 }}>{c.text || "Notice"}</span></div>;
  }
  if (block.type === "button") {
    const solid = c.style === "solid";
    const st: CSSProperties = solid
      ? { minHeight: 48, borderRadius: 12, background: theme.accent, color: qpOnAccent(theme.accent), fontSize: 14.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }
      : { ...P.link2 };
    return <div style={st}><Icon.link size={15}/>{c.label || "Button"}</div>;
  }
  if (block.type === "divider") return <div style={{ height: 1, background: T.border, margin: "2px 0" }} />;
  if (block.type === "spacer") return <div style={{ height: c.size || 18 }} />;
  return null;
}

/** The functional core body for a page (the properties that make it a services
 * / wait / contact / waiting / csat page). Extracted so the builder can wrap it
 * in content blocks and theme it. */
function QPageCore({ page, cfg, ctx, theme }: { page: QPageId; cfg: Record<string, any>; ctx: QPageCtx; theme: QTheme }) {
  const t = useT();
  const c = cfg;
  const cta: CSSProperties = { ...P.cta, background: theme.accent, color: qpOnAccent(theme.accent) };
  if (page === "prebook") {
    return (
      <div style={P.card}>
        <div><div style={P.h1}>{c.headline}</div><div style={{ ...P.sub, marginTop: 4 }}>{c.sub}</div></div>
        {(ctx.questionLabels?.length ? ctx.questionLabels : ["Reason for your visit", "Anything we should prepare?"]).map((q) => <FieldGhost key={q} label={q} />)}
        <div style={cta}>{c.continueLabel ?? "Continue to the queue"}</div>
      </div>
    );
  }
  if (page === "noservice") {
    return (
      <div style={P.card}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
          <span style={{ width: 52, height: 52, borderRadius: 999, background: "#FBEAE7", color: "#C0392B", display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}><Icon.warn size={24}/></span>
          <div style={P.h1}>{c.headline}</div>
          <div style={P.sub}>{c.sub}</div>
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 11, padding: "11px 13px", display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: "#FBEAE7", color: "#C0392B", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.csv size={14}/></span>
          <span style={{ fontSize: 13, fontWeight: 600, color: ctx.surveyName ? T.n1 : T.soft }}>{ctx.surveyName ?? "No survey selected yet"}</span>
        </div>
        <div style={cta}>{c.buttonLabel ?? "Share what happened"}</div>
        {c.showBook !== false ? <div style={P.link2}><Icon.calendar size={15}/>{c.bookLabel ?? "Book an appointment instead"}</div> : null}
        {c.showBrowse !== false ? <div style={P.link2}><Icon.globe size={15}/>{c.browseLabel ?? "Browse our Services and Experiences"}</div> : null}
      </div>
    );
  }
  if (page === "services") {
    return (
      <div style={P.card}>
        <div><div style={P.h1}>{c.headline}</div><div style={{ ...P.sub, marginTop: 4 }}>{c.sub}</div></div>
        <div style={c.layout === "grid" ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 } : { display: "flex", flexDirection: "column", gap: 8 }}>
          {ctx.services.map((s, i) => (
            <div key={s} style={{ border: `1.5px solid ${i === 0 ? theme.accent : T.border}`, background: i === 0 ? alpha(theme.accent, 8) : T.white, borderRadius: 11, padding: "12px 13px", display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{s}</span>
              {c.showEstWait ? <span style={{ fontSize: 11.5, color: T.soft }}>~{ctx.estWaitMins + i * 5} {t('queues.qpPages.text23')}</span> : null}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (page === "locations") {
    const rows: [string, string, string][] = [["Downtown branch", "0.4 km", "12"], ["Riverside plaza", "1.1 km", "5"], ["North mall", "2.6 km", "18"]];
    return (
      <div style={P.card}>
        <div><div style={P.h1}>{c.headline}</div><div style={{ ...P.sub, marginTop: 4 }}>{c.sub}</div></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.slice(0, Math.max(1, Math.min(10, Number(c.maxLocations ?? 10)))).map(([n, d, w], i) => (
            <div key={n} style={{ border: `1.5px solid ${i === 0 ? theme.accent : T.border}`, background: i === 0 ? alpha(theme.accent, 8) : T.white, borderRadius: 11, padding: "12px 13px", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ flex: 1 }}><span style={{ display: "block", fontSize: 14, fontWeight: 700, color: T.n1 }}>{n}</span><span style={{ fontSize: 11.5, color: T.soft }}>{d} {t('queues.qpPages.text24')}</span></span>
              <span style={{ fontSize: 16, fontWeight: 800, color: T.n1, fontFamily: T.fontMono }}>~{w}{t('queues.qpPages.text25')}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (page === "wait") {
    return (
      <div style={P.card}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 6, padding: "6px 0" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#C8861E", textTransform: "uppercase", letterSpacing: ".05em" }}>{ctx.services[0]}</div>
          <div style={P.h1}>{c.headline}</div>
          <div style={{ fontSize: 42, fontWeight: 800, color: T.n1, fontFamily: T.fontMono, lineHeight: 1 }}>~{ctx.estWaitMins}<span style={{ fontSize: 18, fontWeight: 700 }}> {t('queues.qpPages.text26')}</span></div>
          <div style={P.sub}>{ctx.waiting} {t('queues.qpPages.text27')}</div>
        </div>
        <div style={P.sub}>{c.sub}</div>
        <div style={cta}>{c.continueLabel}</div>
        {c.showBook !== false ? <div style={P.link2}><Icon.calendar size={15}/>{c.bookLabel ?? "I can't wait — book an appointment"}</div> : null}
      </div>
    );
  }
  if (page === "contact") {
    const f = (c.fields ?? {}) as QContactFields;
    return (
      <div style={P.card}>
        <div><div style={P.h1}>{c.headline}</div><div style={{ ...P.sub, marginTop: 4 }}>{c.sub}</div></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {f.firstName !== "off" ? <FieldGhost label={t('queues.qpPages.label8')} req={f.firstName === "required"} /> : null}
          {f.lastName !== "off" ? <FieldGhost label={t('queues.qpPages.label9')} req={f.lastName === "required"} /> : null}
        </div>
        {f.mobile !== "off" ? <FieldGhost label={t('queues.qpPages.label10')} req={f.mobile === "required"} /> : null}
        {f.email !== "off" ? <FieldGhost label={t('queues.qpPages.label11')} req={f.email === "required"} /> : null}
        {c.askChannel ? (
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, minHeight: 44, borderRadius: 10, border: "1.5px solid #1D7A3A", background: alpha("#1D7A3A", 10), color: "#1D7A3A", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{t('queues.qpPages.text28')}</div>
            <div style={{ flex: 1, minHeight: 44, borderRadius: 10, border: `1.5px solid ${T.border}`, color: T.n1, fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{t('queues.qpPages.text29')}</div>
          </div>
        ) : null}
        {(ctx.questionLabels ?? []).map((q) => <FieldGhost key={q} label={q} />)}
        <div style={cta}>{c.joinLabel}</div>
      </div>
    );
  }
  if (page === "waiting") {
    return (
      <div style={P.card}>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 6, padding: "6px 0" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: ".05em" }}>{c.headline}</div>
          {c.showPosition ? <div style={{ fontSize: 44, fontWeight: 800, color: T.n1, fontFamily: T.fontMono, lineHeight: 1 }}>#{ctx.position}</div> : null}
          {c.showEstWait ? <div style={{ fontSize: 13.5, color: T.n2 }}>{t('queues.qpPages.text30')}{" "}<b>{ctx.estWaitMins} {t('queues.qpPages.text26')}</b> {t('queues.qpPages.text31')}</div> : null}
        </div>
        {c.allowMessaging ? (
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={P.label}>{t('queues.qpPages.text32')}</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {(c.quickReplies ?? []).slice(0, 3).map((r: string) => <span key={r} style={{ fontSize: 11.5, fontWeight: 600, color: T.n1, border: `1px solid ${T.border}`, borderRadius: 999, padding: "5px 10px", background: T.n6 }}>{r}</span>)}
            </div>
            <div style={{ ...P.input, minHeight: 42, color: T.n5, fontSize: 13 }}>{t('queues.qpPages.text33')}</div>
          </div>
        ) : null}
        {c.showBrowse !== false ? (
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={P.label}>{c.whileHeading ?? "While you wait"}</span>
            <div style={P.link2}><Icon.globe size={15}/>{c.browseLabel ?? "Browse our Services and Experiences"}</div>
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div style={P.card}>
      <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 8, padding: "8px 0" }}>
        <span style={{ width: 52, height: 52, borderRadius: 999, background: "#E4F5EF", color: "#1F8A5B", display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}><Icon.check size={24}/></span>
        <div style={P.h1}>{c.headline}</div>
        <div style={P.sub}>{c.sub}</div>
      </div>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 11, padding: "11px 13px", display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ width: 30, height: 30, borderRadius: 8, background: "#E6F5F1", color: "#0E8A6A", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.csv size={14}/></span>
        <span style={{ fontSize: 13, fontWeight: 600, color: ctx.surveyName ? T.n1 : T.soft }}>{ctx.surveyName ?? "No survey selected yet"}</span>
      </div>
      <div style={cta}>{c.buttonLabel}</div>
      {(c.showBrowse !== false || c.showReview !== false) ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {c.showBrowse !== false ? <div style={P.link2}><Icon.globe size={15}/>{c.browseLabel ?? "Browse our Services and Experiences"}</div> : null}
          {c.showReview !== false ? <div style={P.link2}><Icon.star size={15}/>{c.reviewLabel ?? "Leave a Google Review"}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/** Static page renderer — canvas minis + builder preview. Renders the block
 * stack (brand + content blocks + functional core) with the page theme. When
 * `selectedUid`/`onSelect` are passed it becomes an interactive canvas (blocks
 * are click-selectable with a highlight ring). */
export function QPageView({ page, cfg, ctx = QP_SAMPLE_CTX, selectedUid, onSelect }:
  { page: QPageId; cfg: Record<string, unknown>; ctx?: QPageCtx; selectedUid?: string | null; onSelect?: (uid: string) => void }) {
  const c = cfg as Record<string, any>;
  const theme = qpTheme(c);
  const blocks = qpBlocks(c);
  const pageStyle: CSSProperties = { width: 375, minHeight: 560, background: theme.bg, fontFamily: qpFontFamily(theme), display: "flex", flexDirection: "column", boxSizing: "border-box", padding: "18px 14px 22px", gap: 12 };
  const interactive = !!onSelect;
  return (
    <div style={pageStyle}>
      {blocks.map((b) => {
        const node = b.type === "brand" ? <Brand ctx={ctx} theme={theme} />
          : b.type === "core" ? <QPageCore page={page} cfg={c} ctx={ctx} theme={theme} />
          : <QContentBlock block={b} theme={theme} />;
        if (!interactive) return <div key={b.uid}>{node}</div>;
        const on = selectedUid === b.uid;
        return (
          <div key={b.uid} onClick={(e) => { e.stopPropagation(); onSelect!(b.uid); }}
            style={{ position: "relative", borderRadius: 12, outline: on ? `2px solid ${T.p1}` : "2px solid transparent", outlineOffset: 3, cursor: "pointer", transition: "outline-color .12s" }}>
            {node}
          </div>
        );
      })}
    </div>
  );
}

/** Resolve a queue-page design's landing theme. "brand" uses the snapshot the
 * builder saved into the design (design.brandTheme) so canvas minis AND the
 * public join surface render the tenant look without needing admin branding at
 * read time (WYSIWYG end-to-end). */
export function qDesignTheme(design?: { theme?: string; brandTheme?: LandingTheme }): LandingTheme {
  const bt = design?.brandTheme;
  if (design?.theme === "brand" && bt?.brand) return bt;
  return LANDING_THEMES.find((t) => t.id === design?.theme) || (bt?.brand ? bt : LANDING_THEMES[0]);
}

/** Scaled phone miniature — the canvas node body + pickers. Always previews
 * the REAL page (operator direction 2026-07-20): the saved landing-style
 * design when present, else the same default design the Page Builder seeds
 * (hero + the page's functional block) — never the generic representative
 * card. Functional blocks render via QueueBlockView on a sample runtime. */
export function QPageMini({ page, cfg, scale = 0.34, height, rt: rtOverride }: { page: QPageId; cfg: Record<string, unknown>; ctx?: QPageCtx; scale?: number; height?: number; rt?: Partial<QRuntime> }) {
  const t = useT();
  const c = cfg as Record<string, any>;
  const branding = useBranding() as { primary?: string; cover?: string; bodyFont?: string } | undefined;
  const design = c?.design as { blocks?: any[]; theme?: string; font?: string; brandTheme?: LandingTheme } | undefined;
  const saved = design && Array.isArray(design.blocks) && design.blocks.length ? design : null;
  const rt = useMemo(() => ({ ...qSampleRuntime(t), ...(rtOverride ?? {}) }), [rtOverride, t]);
  const blocks = useMemo(
    () => saved ? saved.blocks! : queueDesignDefault(page, c, rtOverride?.heroImage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saved, page, cfg, rtOverride?.heroImage],
  );
  const theme: LandingTheme = saved
    ? qDesignTheme(saved)
    : { id: "brand", name: "Brand" /* ui-config — theme identifier default, seeded into saved docs */, brand: branding?.primary || T.p1, hero: [branding?.primary || "#75ADFA", branding?.cover || T.p1], font: branding?.bodyFont || "DM Sans" };
  const LW = 420;
  const s = (375 * scale) / LW;
  // Fixed `height` crops (canvas-node thumbnails); without it the mini sizes
  // itself to the FULL page — all services/content visible, however long.
  const innerRef = useRef<HTMLDivElement>(null);
  const [autoH, setAutoH] = useState(118);
  useEffect(() => {
    if (height != null) return;
    const el = innerRef.current;
    if (!el) return;
    const measure = () => setAutoH(Math.max(80, Math.ceil(el.offsetHeight * s)));
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [height, s, blocks]);
  return (
    <div style={{ width: 375 * scale, height: height ?? autoH, overflow: "hidden", borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, pointerEvents: "none", flexShrink: 0 }}>
      <div ref={innerRef} style={{ width: LW, transform: `scale(${s})`, transformOrigin: "top left" }}>
        <LandingPreview blocks={blocks} theme={theme} font={saved?.font || "DM Sans"} event={null} chrome={false}
          renderCustom={(b: { type: string }) => isQueueBlock(b.type) ? <QueueBlockView block={b as any} theme={theme} rt={rt} /> : null} />
      </div>
    </div>
  );
}
